import { createPostAssociation } from "deso-protocol";
import { use, useCallback, useState } from "react";

import { waitForTxn } from "@/backend/api-helpers";
import { Spinner } from "@/components/shared/spinner";
import { useToast } from "@/components/ui/use-toast";
import { usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import { AssociationType } from "@/utils/constants";

type PostActivePollProps = {
  options: string[];
  onUserVote: () => void;
};

export const PostActivePoll = ({
  options,
  onUserVote,
}: PostActivePollProps) => {
  const { currentUserPublicKey } = use(UserContext);
  const [loading, setLoading] = useState(false);
  const { isPureRepost, post, repost } = usePostContext();
  const { toast } = useToast();
  const postHash = isPureRepost ? repost?.postHash : post?.postHash;

  if (!postHash) {
    throw new Error("Post hash is missing.");
  }

  const vote = useCallback(
    async (option: string) => {
      if (loading) return;

      setLoading(true);
      try {
        const data = await createPostAssociation({
          AssociationType: AssociationType.pollResponse,
          AssociationValue: option,
          TransactorPublicKeyBase58Check: currentUserPublicKey,
          PostHashHex: postHash,
        });

        if (!data.submittedTransactionResponse?.TxnHashHex) {
          throw new Error("Transaction was not constructed. Please try again.");
        }

        await waitForTxn({
          transactionHash: data.submittedTransactionResponse?.TxnHashHex,
        });

        toast({
          title: "Success",
          description: "Your vote has been successfully recorded",
          variant: "success",
        });

        onUserVote();
      } catch {
        toast({
          title: "Error",
          description: "An error occurred while voting",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    [loading, onUserVote, postHash, toast, currentUserPublicKey],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-2">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {options.map((option, index) => (
        <div
          key={`${option}-${index}`}
          onClick={() => {
            vote(option);
          }}
          className="
            flex w-full cursor-pointer items-center justify-center gap-2
            overflow-hidden rounded-full border border-border bg-card p-2 pr-0
            text-foreground
            hover:border-primary hover:bg-background hover:text-foreground
          "
          role="button"
        >
          {loading && <Spinner />}
          <p className="flex items-center gap-2 text-sm font-normal">
            {option}
          </p>
        </div>
      ))}
    </div>
  );
};
